import {
  type QuantityKind,
  transformFor,
  type UnitFor,
  type UnitTransform,
} from './registry';

/**
 * The one conversion function. Pure arithmetic: no locale, no rounding, no formatting.
 *
 * Rounding lives in `precision.ts` as a digit count and is applied once, by the formatter, at the
 * point of display. Converting and rounding in the same step is how a value picks up error before
 * anyone has done anything with it — and it is why sorting a table by a converted column is exact
 * here, where today's helpers round inside the conversion and sort the rounded values.
 */

/** value -> canonical. Each transform describes this direction; the inverse is derived below. */
const toCanonical = (transform: UnitTransform, value: number): number => {
  switch (transform.family) {
    case 'linear':
      return value * transform.factor;
    case 'affine':
      return value * transform.factor + transform.offset;
    case 'reciprocal':
      return transform.numerator / value;
  }
};

/**
 * canonical -> value. The exact inverse of the above, derived rather than declared.
 *
 * A reciprocal is its own inverse for a given numerator, which is why the two branches look
 * identical: `n / (n / x)` is `x`. Deriving both directions from one declaration is what makes it
 * impossible for a unit's forward and backward conversions to disagree.
 */
const fromCanonical = (transform: UnitTransform, canonical: number): number => {
  switch (transform.family) {
    case 'linear':
      return canonical / transform.factor;
    case 'affine':
      return (canonical - transform.offset) / transform.factor;
    case 'reciprocal':
      return transform.numerator / canonical;
  }
};

/**
 * Converts a measurement between two units of the same kind.
 *
 * Binding `from` and `to` to `kind` makes a cross-quantity conversion unrepresentable: there is no
 * way to pass `'PSI'` where a distance belongs, so the function has no invalid-unit path to guard
 * and no error to report. The units come from the same closed unions the preference contract
 * resolves, so a value read out of `ResolvedPreferences` is accepted directly.
 *
 * Non-finite values are propagated rather than rejected, because for the reciprocal kinds they are
 * answers rather than mistakes. Zero consumption really is infinite economy, and the engine says
 * so instead of quietly returning zero the way `convertFuelEconomy` does today. Feeding that
 * `Infinity` back in returns the original zero, so the round trip holds. `NaN` propagates for the
 * same reason: an unusable input should stay visibly unusable rather than become a plausible
 * number. Rendering either as a placeholder is the formatter's job, not this function's.
 *
 * @param kind - The quantity being converted.
 * @param value - The measurement, in `from` units.
 * @param from - The unit the value is currently in.
 * @param to - The unit wanted.
 * @returns The measurement in `to` units, unrounded.
 */
export const convert = <K extends QuantityKind>(
  kind: K,
  value: number,
  from: UnitFor<K>,
  to: UnitFor<K>
): number => {
  // Not merely a shortcut: it guarantees a same-unit conversion is bit-identical, where a round
  // trip through the canonical unit could return 0.30000000000000004 for 0.3.
  if (from === to) return value;

  return fromCanonical(
    transformFor(kind, to),
    toCanonical(transformFor(kind, from), value)
  );
};
